'use client';

import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import { useAllIfData } from './query/ifData/useAllIfData';

const useTvl = () => {
	const borrowLendData = useDriftStore((s) => s.borrowLendData);
	const { data: ifStakeData } = useAllIfData();

	const depositTvl = useMemo(() => {
		const totalBN = borrowLendData.reduce((previousTotal, dataForMarket) => {
			return dataForMarket.totalDepositsQuote.val.add(previousTotal);
		}, ZERO);

		return BigNum.from(totalBN, QUOTE_PRECISION_EXP);
	}, [borrowLendData]);

	const borrowTvl = useMemo(() => {
		const totalBN = borrowLendData.reduce((previousTotal, dataForMarket) => {
			return dataForMarket.totalBorrowsQuote.val.add(previousTotal);
		}, ZERO);

		return BigNum.from(totalBN, QUOTE_PRECISION_EXP);
	}, [borrowLendData]);

	const ifStakeTvl = useMemo(() => {
		// Converting to num as simplest way of dealing with differing precisions
		const vaultBalances = Object.values(ifStakeData).map((ifStakeForMarket) => {
			if (!ifStakeForMarket?.vault.loaded) return 0;

			return ifStakeForMarket?.vault?.totalStakeNotionalValue?.toNum() ?? 0;
		});

		const total = vaultBalances.reduce((previousTotal, currentVaultTotal) => {
			return previousTotal + currentVaultTotal;
		}, 0);

		return BigNum.fromPrint(`${total}`, QUOTE_PRECISION_EXP);
	}, [ifStakeData]);

	const tvlTotal = depositTvl.add(ifStakeTvl).sub(borrowTvl);

	return {
		depositTvl,
		borrowTvl,
		ifStakeTvl,
		tvlTotal,
	};
};

export default useTvl;
